import React, { useEffect, useState } from "react";
import "./TicketCaisse.css";
import { Button, Modal, Select } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from '../config/api.js';
import QRCode from "qrcode";
import { buildInvoiceVerificationUrl } from "../utils/invoiceVerificationUrl";

const { Option } = Select;

interface TicketCaisseProps {
  visible: boolean;
  order: any;
  onClose: () => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return (
    date.toLocaleDateString("fr-FR") + " " + date.toLocaleTimeString("fr-FR")
  );
};

const formatAmountCurrency = (amount: number) => {
  return (
    amount.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " CFA"
  );
};

// Nouvelle fonction pour formater sans devise (optimisation papier)
const formatAmount = (amount: number) => {
  return amount.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const resolveBooleanFlag = (value: unknown, fallback = true) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }

  return Number(value) !== 0;
};

const resolveVerificationBaseUrl = () => {
  const publicBase =
    (process.env.REACT_APP_PUBLIC_BASE_URL || "").trim();
  const windowBase =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  const apiBase = (API_CONFIG?.BASE_URL || "").trim();

  let baseUrl = publicBase || windowBase || apiBase;

  if (!baseUrl || /localhost|127\.0\.0\.1/i.test(baseUrl)) {
    if (apiBase && !/localhost|127\.0\.0\.1/i.test(apiBase)) {
      baseUrl = apiBase;
    } else {
      baseUrl = "https://elsagestion.com";
    }
  }

  return baseUrl.replace(/\/$/, "");
};

const TicketCaisse: React.FC<TicketCaisseProps> = ({
  visible,
  order,
  onClose,
}) => {
  const [warehouseInfo, setWarehouseInfo] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string>("/logo-placeholder.png");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const [receiptWidthMm, setReceiptWidthMm] = useState<number>(() => {
    const stored = Number(localStorage.getItem("posReceiptWidthMm"));
    return stored == 58 || stored == 80 ? stored : 80;
  });

  // Déterminer si l'écran est très petit (mobile étroit) pour la largeur de la modale
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(
    window.innerWidth < 400
  );

  useEffect(() => {
    const handleResize = () => {
      setIsVerySmallScreen(window.innerWidth < 400);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem("posReceiptWidthMm", String(receiptWidthMm));
  }, [receiptWidthMm]);

  const shouldShowQrCodeOnReceipt = resolveBooleanFlag(
    warehouseInfo?.show_qr_code_on_receipt ??
      order?.warehouse?.show_qr_code_on_receipt,
    true
  );

  // Generer un QR code de verification pour la facture
  useEffect(() => {
    const invoiceNumber = order?.order?.invoice_number;
    const verificationToken = order?.order?.verification_token;
    if (
      !visible ||
      !invoiceNumber ||
      !verificationToken ||
      !shouldShowQrCodeOnReceipt
    ) {
      setQrCodeDataUrl("");
      return;
    }

    let cancelled = false;
    const verificationUrl = buildInvoiceVerificationUrl({
      baseUrl: resolveVerificationBaseUrl(),
      invoiceNumber,
      verificationToken,
    });

    QRCode.toDataURL(verificationUrl, {
      type: "image/png",
      width: 180,
      margin: 1,
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    })
      .then((dataUrl: string) => {
        if (!cancelled) {
          setQrCodeDataUrl(dataUrl);
        }
      })
      .catch((error: unknown) => {
        console.error("Erreur generation QR code ticket:", error);
        if (!cancelled) {
          setQrCodeDataUrl("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    visible,
    order?.order?.invoice_number,
    order?.order?.verification_token,
    shouldShowQrCodeOnReceipt,
  ]);


  // Récupérer les informations complètes de l'entrepôt si un ID est spécifié
  useEffect(() => {
    if (visible && order && order.order && order.order.warehouse_id) {
      const fetchWarehouseInfo = async () => {
        try {
          const response = await axios.get(
            `${API_CONFIG.BASE_URL}/api/warehouses/${order.order.warehouse_id}`
          );
          if (response.status === 200 && response.data) {

            setWarehouseInfo(response.data);

            // Déterminer l'URL correcte du logo
            let logo = response.data.logo;
            if (logo) {
              // Vérifier si le chemin commence par http ou https
              if (!logo.startsWith("http") && !logo.startsWith("/")) {
                logo = `/${logo}`;
              }
              // Vérifier si le logo est stocké dans le dossier uploads/warehouses
              if (logo.includes("uploads/warehouses")) {
                logo = `${API_CONFIG.BASE_URL}${logo}`;
              } else if (logo.startsWith("/uploads/warehouses")) {
                logo = `${API_CONFIG.BASE_URL}${logo}`;
              }
              setLogoUrl(logo);
            } else if (order.warehouse?.logo_url) {
              let warehouseLogo = order.warehouse.logo_url;
              if (
                !warehouseLogo.startsWith("http") &&
                !warehouseLogo.startsWith("/")
              ) {
                warehouseLogo = `/${warehouseLogo}`;
              }
              if (warehouseLogo.includes("uploads/warehouses")) {
                warehouseLogo = `${API_CONFIG.BASE_URL}${warehouseLogo}`;
              } else if (warehouseLogo.startsWith("/uploads/warehouses")) {
                warehouseLogo = `${API_CONFIG.BASE_URL}${warehouseLogo}`;
              }
              setLogoUrl(warehouseLogo);
  
            }
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des infos de l'établissement:",
            error
          );
          // Utiliser le logo de l'ordre si disponible
          if (order.warehouse?.logo_url) {
            let warehouseLogo = order.warehouse.logo_url;
            if (
              !warehouseLogo.startsWith("http") &&
              !warehouseLogo.startsWith("/")
            ) {
              warehouseLogo = `/${warehouseLogo}`;
            }
            if (warehouseLogo.includes("uploads/warehouses")) {
              warehouseLogo = `${API_CONFIG.BASE_URL}${warehouseLogo}`;
            } else if (warehouseLogo.startsWith("/uploads/warehouses")) {
              warehouseLogo = `${API_CONFIG.BASE_URL}${warehouseLogo}`;
            }
            setLogoUrl(warehouseLogo);
          }
        }
      };

      fetchWarehouseInfo();
    }
  }, [visible, order]);

  if (!visible || !order) {
    return null;
  }



  // Utiliser les informations de l'entrepôt récupérées ou les valeurs par défaut
  const warehouseName =
    warehouseInfo?.name || order.warehouse?.name || "Votre Entreprise";
  const warehouseAddress =
    warehouseInfo?.address ||
    order.warehouse?.address ||
    "Adresse de l'entreprise";
  const warehousePhone =
    warehouseInfo?.phone || order.warehouse?.phone || "Telephone";
  const warehouseEmail =
    warehouseInfo?.email || order.warehouse?.email || "Email";

  // Informations client
  const customerName = order.customer?.name || "Client";

  // Montants de paiement
  const paidAmount = order.order?.paid_amount || 0;
  const dueAmount = order.order?.due_amount || 0;
  const paymentStatusRaw = order.order?.payment_status ?? "";

  const normalizeStatus = (status: string) =>
    status
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // Obtenir l'etiquette et la couleur du statut de paiement (robuste aux variantes)
  const paymentStatusInfo = (() => {
    const normalized = normalizeStatus(paymentStatusRaw);

    if (normalized.includes("part") || normalized.includes("partial")) {
      return { label: "Partiellement paye", color: "#faad14" };
    }

    if (
      normalized.startsWith("pay") ||
      normalized === "paid" ||
      normalized === "settled" ||
      normalized === "completed"
    ) {
      return { label: "Paye", color: "#52c41a" };
    }

    if (
      normalized.includes("non") ||
      normalized.includes("unpaid") ||
      normalized.includes("not")
    ) {
      return { label: "Non paye", color: "#ff4d4f" };
    }

    return {
      label: paymentStatusRaw ? paymentStatusRaw.toString() : "Non paye",
      color: "#ff4d4f",
    };
  })();

  const receiptPaperWidthMm = Number(order.order?.paper_width_mm || warehouseInfo?.paper_width_mm || order.warehouse?.paper_width_mm || receiptWidthMm || 80);
  const receiptPaddingMm = 2;
  const receiptTitle = "=== TICKET DE CAISSE ===";
  const receiptThanks = "* MERCI POUR VOTRE ACHAT *";
  const isNarrowPaper = receiptPaperWidthMm <= 60;
  const previewMaxWidth = isNarrowPaper ? "220px" : "280px";
  const baseFontSize = isNarrowPaper ? 14 : 13;
  const itemFontSize = isNarrowPaper ? 12 : 11;
  const infoFontSize = isNarrowPaper ? 12 : 11;
  const logoWidth = isNarrowPaper ? 90 : 110;
  const qrSizePx = isNarrowPaper ? 70 : 90;

  const totalItems =
    order.items?.reduce((sum: number, item: any) => sum + 1, 0) || 0;
  const totalQuantity =
    order.items?.reduce(
      (sum: number, item: any) => sum + (parseFloat(item.quantity) || 0),
      0
    ) || 0;

  const printTicket = () => {
    // Créer le contenu du ticket
    const ticketHTML = `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8" />
      <title>Ticket de Caisse</title>
      <style>
        @page { size: ${receiptPaperWidthMm}mm auto; margin: 0; }
        * { box-sizing: border-box; }
        html, body { width: ${receiptPaperWidthMm}mm; }
        body {
          font-family: "Courier New", "Consolas", monospace;
          font-size: ${baseFontSize}px;
          font-weight: 700;
          width: ${receiptPaperWidthMm}mm;
          margin: 0;
          padding: ${receiptPaddingMm}mm;
          line-height: 1.35;
          color: #000;
          background: #fff;
          -webkit-font-smoothing: none;
          text-rendering: optimizeSpeed;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .ticket-header { text-align: center; margin: 2mm 0 1mm 0; }
        .ticket-logo { width: ${logoWidth}px; max-width: 100%; height: auto; margin: 1mm auto; display: block; }
        .ticket-company { text-align: center; margin: 1mm 0 2mm 0; border-bottom: 1px solid #000; padding-bottom: 2mm; }
        .ticket-company h2 { margin: 0.5mm 0; font-size: 14px; font-weight: 700; }
        .ticket-company p { margin: 0.5mm 0; font-size: ${infoFontSize}px; }
        .ticket-title { text-align: center; margin: 2mm 0; font-weight: 700; font-size: 12px; letter-spacing: 0.3px; }
        .ticket-info { margin: 1.5mm 0; font-size: ${infoFontSize}px; }
        .ticket-info table { width: 100%; table-layout: fixed; }
        .ticket-info td { padding: 0.5mm 0; vertical-align: top; }
        .divider { border-top: 1px dashed #000; margin: 2mm 0; }
        .ticket-items { margin: 1mm 0; }
        .ticket-items table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: ${itemFontSize}px; }
        .ticket-items thead td { font-weight: 700; padding: 0.8mm 0; border-bottom: 1px solid #000; }
        .ticket-item { border-bottom: 1px solid #000; }
        .ticket-item td { padding: 0.8mm 0; vertical-align: top; }
        .ticket-item td:nth-child(2) { word-break: break-word; }
        .ticket-summary { margin: 2mm 0; border-top: 1px solid #000; padding-top: 1.5mm; }
        .ticket-summary table { width: 100%; font-size: 11px; }
        .ticket-summary td { padding: 0.5mm 0; }
        .ticket-total { font-weight: 700; font-size: 12px; border-top: 2px solid #000; padding-top: 1mm; margin-top: 1mm; }
        .ticket-payment { margin: 2mm 0; text-align: center; font-size: ${infoFontSize}px; }
        .ticket-payment table { width: 100%; }
        .ticket-payment td { padding: 0.6mm; }
        .ticket-qrcode { text-align: center; margin: 1.5mm 0; }
        .ticket-qrcode img { width: ${qrSizePx}px; height: ${qrSizePx}px; display: block; margin: 0 auto; }
        .ticket-qrcode .ticket-qrcode-label { font-size: 8px; margin-top: 1mm; }
        .ticket-currency { text-align: center; font-size: 11px; font-weight: 700; margin: 2mm 0; border: 1px solid #000; padding: 1mm; }
        .ticket-footer { margin-top: 2.5mm; text-align: center; font-size: 9.5px; }
        .ticket-thanks { font-weight: 700; margin: 2mm 0; }
        .ticket-signature { border-top: 1px dashed #000; padding-top: 2mm; margin-top: 2mm; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .bold { font-weight: 700; }
      </style>
    </head>
    <body>
      <div class="ticket-header">
        <img class="ticket-logo" src="${logoUrl}" alt="Logo" />
      </div>
      
      <div class="ticket-company">
        <h2>${warehouseName}</h2>
        <p>${warehouseAddress}</p>
        <p>Tel: ${warehousePhone} | ${warehouseEmail}</p>
      </div>
      
      <div class="ticket-title">${receiptTitle}</div>
      
      <div class="ticket-info">
        <table>
          <tbody>
            <tr>
              <td><b>Facture:</b> ${order.order?.invoice_number}</td>
              <td class="text-right"><b>Client:</b> ${customerName}</td>
            </tr>
            <tr>
              <td><b>Date:</b> ${formatDate(
                order.order?.order_date || new Date().toISOString()
              )}</td>
              <td class="text-right"><b>Vendeur:</b> ${order.staff_member?.name || "Vendeur"}</td>
            </tr>
            ${
              order.server?.name
                ? `<tr>
              <td colspan="2"><b>Serveur:</b> ${order.server.name}</td>
            </tr>`
                : ""
            }
            ${
              order.order?.source_order_number
                ? `<tr>
              <td colspan="2"><b>Cmd. restaurant:</b> ${order.order.source_order_number}</td>
            </tr>`
                : ""
            }
          </tbody>
        </table>
      </div>
      
      <div class="divider"></div>
      
      <div class="ticket-items">
        <table>
          <thead>
            <tr>
              <td style="width: 6%">#</td>
              <td style="width: 44%">Article</td>
              <td style="width: 10%">Qte</td>
              <td style="width: 16%">Prix</td>
              <td style="width: 24%; text-align: right">Total</td>
            </tr>
          </thead>
          <tbody>
            ${order.items
              ?.map(
                (item: any, index: number) => `
              <tr class="ticket-item">
                <td>${index + 1}</td>
                <td>${item.product?.name || item.nom_produit}</td>
                <td>${item.quantity || item.cartQuantity}</td>
                <td>${formatAmount(item.unit_price || item.prix_vente)}</td>
                <td class="text-right">${formatAmount(
                  (item.unit_price || item.prix_vente) *
                    (item.quantity || item.cartQuantity)
                )}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
      
      <div class="ticket-summary">
        <table>
          <tbody>
            <tr>
              <td>Sous-total:</td>
              <td class="text-right">${formatAmount(order.order?.subtotal || 0)}</td>
            </tr>
            ${order.order?.tax_amount > 0 ? `<tr>
              <td>Taxe:</td>
              <td class="text-right">${formatAmount(order.order?.tax_amount)}</td>
            </tr>` : ''}
            ${order.order?.discount > 0 ? `<tr>
              <td>Remise:</td>
              <td class="text-right">-${formatAmount(order.order?.discount)}</td>
            </tr>` : ''}
          </tbody>
        </table>
        
        <div class="ticket-total">
          <table>
            <tbody>
              <tr>
                <td>TOTAL:</td>
                <td class="text-right">${formatAmount(order.order?.total || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="ticket-currency">
        DEVISE: FRANCS CFA (XOF)
      </div>
      
      <div class="ticket-payment">
        <table>
          <tbody>
            <tr>
              <td><b>Paye:</b> ${formatAmount(paidAmount)}</td>
              <td class="text-right"><b>Du:</b> ${formatAmount(dueAmount)}</td>
            </tr>
            <tr>
              <td colspan="2" class="text-center">
                <b>Mode:</b> ${order.order?.payment_mode || "Especes"} | 
                <b>Statut:</b> <span style="color: ${paymentStatusInfo.color}; font-weight: bold;">${paymentStatusInfo.label}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="divider"></div>

      ${qrCodeDataUrl ? `
      <div class="ticket-qrcode">
        <img src="${qrCodeDataUrl}" alt="QR Code verification" />
        <div class="ticket-qrcode-label">Verification facture</div>
      </div>
      ` : ""}

      <div class="ticket-footer">
        <div class="ticket-thanks">
          ${receiptThanks}
        </div>
        
        <div class="ticket-signature">
          <p class="text-center"><b>Solutions ELSA GESTION</b></p>
          <p class="text-center">Support: +226 64 98 90 99</p>
          <p class="text-center">www.elsagestion.com</p>
        </div>
        
        <div class="text-center" style="margin-top: 2mm; font-size: 8px;">
          ${order.order?.invoice_number} | ${new Date().toLocaleDateString("fr-FR")} ${new Date().toLocaleTimeString("fr-FR", {hour: '2-digit', minute:'2-digit'})}
        </div>
      </div>
    </body>
    </html>
    `;

    // Ouvrir une nouvelle fenetre du navigateur et y écrire le contenu du ticket
    const newWindow = window.open(
      "",
      "_blank",
      "width=300,height=600,toolbar=no,scrollbars=yes"
    );
    if (newWindow) {
      newWindow.document.open();
      newWindow.document.write(ticketHTML);
      newWindow.document.close();

      const triggerPrint = () => {
        newWindow.focus();
        newWindow.print();
      };

      const images = Array.from(newWindow.document.images || []);
      if (images.length === 0) {
        setTimeout(triggerPrint, 300);
      } else {
        let loaded = 0;
        const onResourceReady = () => {
          loaded += 1;
          if (loaded >= images.length) {
            setTimeout(triggerPrint, 200);
          }
        };
        images.forEach((img) => {
          if (img.complete) {
            onResourceReady();
          } else {
            img.onload = onResourceReady;
            img.onerror = onResourceReady;
          }
        });
      }
    } else {

      console.error("Impossible d'ouvrir une nouvelle fenetre");
    }
  };

  return (
    <Modal
      title="Ticket de Caisse"
      open={visible}
      onCancel={onClose}
      width={isVerySmallScreen ? "95%" : 320}
      style={{ top: 20 }}
      centered
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={printTicket}
        >
          Imprimer
        </Button>,
      ]}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "8px" }}>
        <div style={{ fontSize: "12px", marginRight: "8px", alignSelf: "center" }}>Format ticket:</div>
        <Select
          size="small"
          value={receiptWidthMm}
          onChange={(value: number) => setReceiptWidthMm(value)}
          style={{ width: 120 }}
        >
          <Option value={58}>58 mm</Option>
          <Option value={80}>80 mm</Option>
        </Select>
      </div>
      <div
        className="ticket-caisse-preview"
        style={{
          overflowX: "auto",
          maxHeight: "70vh",
          overflowY: "auto",
          padding: "10px",
          background: "#f9f9f9",
          // Style pour simuler la largeur d'un ticket de caisse (80mm ~ 300px)
          width: "100%",
          maxWidth: previewMaxWidth,
          margin: "0 auto",
          fontSize: isNarrowPaper ? "14px" : "13px",
          fontWeight: 700,
          fontFamily: "Courier New, Consolas, monospace",
        }}
      >
        <div style={{ textAlign: "center", margin: "12px 0 8px 0" }}>
          <img
            style={{
              width: "120px",
              maxWidth: "100%",
              height: "auto",
              margin: "8px auto",
              display: "block",
            }}
            src={logoUrl}
            alt="Logo"
          />
        </div>

        <div
          style={{
            textAlign: "center",
            margin: "8px 0",
            borderBottom: "1px solid #000",
            paddingBottom: "8px",
          }}
        >
          <h2 style={{ margin: "4px 0", fontSize: "14px", fontWeight: "bold" }}>
            {warehouseName}
          </h2>
          <p style={{ margin: "2px 0", fontSize: "10px" }}>{warehouseAddress}</p>
          <p style={{ margin: "2px 0", fontSize: "10px" }}>
            Tel: {warehousePhone} | {warehouseEmail}
          </p>
        </div>

        <div style={{ textAlign: "center", margin: "8px 0", fontWeight: "bold", fontSize: "12px" }}>
          {receiptTitle}
        </div>

        <div style={{ margin: "8px 0", fontSize: "10px" }}>
          <table style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td>
                  <b>Facture:</b> {order.order?.invoice_number}
                </td>
                <td style={{ textAlign: "right" }}>
                  <b>Client:</b> {customerName}
                </td>
              </tr>
              <tr>
                <td>
                  <b>Date:</b> {formatDate(order.order?.order_date || new Date().toISOString())}
                </td>
                <td style={{ textAlign: "right" }}>
                  <b>Vendeur:</b> {order.staff_member?.name || "Vendeur"}
                </td>
              </tr>
              {order.server?.name && (
                <tr>
                  <td colSpan={2}>
                    <b>Serveur:</b> {order.server.name}
                  </td>
                </tr>
              )}
              {order.order?.source_order_number && (
                <tr>
                  <td colSpan={2}>
                    <b>Cmd. restaurant:</b> {order.order.source_order_number}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ borderTop: "1px dashed #000", margin: "8px 0" }}></div>

        {qrCodeDataUrl && (
          <div style={{ textAlign: "center", margin: "6px 0" }}>
            <img
              src={qrCodeDataUrl}
              alt="QR Code verification"
              style={{
                width: `${qrSizePx}px`,
                height: `${qrSizePx}px`,
                display: "block",
                margin: "0 auto",
              }}
            />
            <div style={{ fontSize: "8px", marginTop: "4px" }}>
              Verification facture
            </div>
          </div>
        )}

        <div style={{ margin: "4px 0" }}>
          <table style={{ width: "100%", fontSize: "10px" }}>
            <thead>
              <tr>
                <td style={{ fontWeight: "bold", borderBottom: "1px solid #000", padding: "2px 0" }}>#</td>
                <td style={{ fontWeight: "bold", borderBottom: "1px solid #000", padding: "2px 0" }}>Article</td>
                <td style={{ fontWeight: "bold", borderBottom: "1px solid #000", padding: "2px 0" }}>Qte</td>
                <td style={{ fontWeight: "bold", borderBottom: "1px solid #000", padding: "2px 0" }}>Prix</td>
                <td
                  style={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #000",
                    padding: "2px 0",
                    textAlign: "right",
                  }}
                >
                  Total
                </td>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item: any, index: number) => (
                <tr key={index} style={{ borderBottom: "1px solid #000" }}>
                  <td style={{ padding: "2px 0" }}>{index + 1}</td>
                  <td style={{ padding: "2px 0" }}>{item.product?.name || item.nom_produit}</td>
                  <td style={{ padding: "2px 0" }}>{item.quantity || item.cartQuantity}</td>
                  <td style={{ padding: "2px 0" }}>{formatAmount(item.unit_price || item.prix_vente)}</td>
                  <td style={{ padding: "2px 0", textAlign: "right" }}>
                    {formatAmount((item.unit_price || item.prix_vente) * (item.quantity || item.cartQuantity))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ margin: "8px 0", borderTop: "1px solid #000", paddingTop: "8px" }}>
          <table style={{ width: "100%", fontSize: "11px" }}>
            <tbody>
              <tr>
                <td>Sous-total:</td>
                <td style={{ textAlign: "right" }}>{formatAmount(order.order?.subtotal || 0)}</td>
              </tr>
              {(order.order?.tax_amount || 0) > 0 && (
                <tr>
                  <td>Taxe:</td>
                  <td style={{ textAlign: "right" }}>{formatAmount(order.order?.tax_amount || 0)}</td>
                </tr>
              )}
              {(order.order?.discount || 0) > 0 && (
                <tr>
                  <td>Remise:</td>
                  <td style={{ textAlign: "right" }}>-{formatAmount(order.order?.discount || 0)}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ fontWeight: "bold", fontSize: "12px", borderTop: "2px solid #000", paddingTop: "4px", marginTop: "4px" }}>
            <table style={{ width: "100%" }}>
              <tbody>
                <tr>
                  <td>TOTAL:</td>
                  <td style={{ textAlign: "right" }}>{formatAmount(order.order?.total || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: "11px", fontWeight: "bold", margin: "8px 0", border: "1px solid #000", padding: "4px" }}>
          DEVISE: FRANCS CFA (XOF)
        </div>

        <div style={{ margin: "8px 0", textAlign: "center", fontSize: "10px" }}>
          <table style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td>
                  <b>Paye:</b> {formatAmount(paidAmount)}
                </td>
                <td style={{ textAlign: "right" }}>
                  <b>Du:</b> {formatAmount(dueAmount)}
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={{ textAlign: "center", paddingTop: "4px" }}>
                  <b>Mode:</b> {order.order?.payment_mode || "Especes"} | <b>Statut:</b>{" "}
                  <span style={{ color: paymentStatusInfo.color, fontWeight: "bold" }}>
                    {paymentStatusInfo.label}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ borderTop: "1px dashed #000", margin: "8px 0" }}></div>

        <div style={{ textAlign: "center", fontSize: "9px" }}>
          <div style={{ fontWeight: "bold", margin: "8px 0" }}>
            {receiptThanks}
          </div>

          <div style={{ borderTop: "1px dashed #000", paddingTop: "8px", marginTop: "8px" }}>
            <p style={{ margin: "2px 0" }}>
              <b>Solutions ELSA GESTION</b>
            </p>
            <p style={{ margin: "2px 0" }}>Support: +226 64 98 90 99</p>
            <p style={{ margin: "2px 0" }}>www.elsagestion.com</p>
          </div>

          <div style={{ marginTop: "8px", fontSize: "8px" }}>
            {order.order?.invoice_number} | {new Date().toLocaleDateString("fr-FR")} {new Date().toLocaleTimeString("fr-FR", {hour: '2-digit', minute:'2-digit'})}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TicketCaisse;
